/**
 * CSRF 토큰 자동 주입 인터셉터 (SEC-12)
 *
 * CookieCsrfTokenRepository가 설정한 XSRF-TOKEN 쿠키를 읽어
 * POST/PUT/DELETE/PATCH 요청의 X-XSRF-TOKEN 헤더에 자동 주입한다.
 *
 * 커버 범위:
 *   1) window.fetch  — 모든 fetch() 호출 자동 적용
 *   2) XMLHttpRequest — 모든 XHR 호출 자동 적용
 *   3) WsCsrf.appendToForm() — 동적 생성 form에 hidden 필드 수동 삽입
 *
 * 함께 맡는 일 (IMP-001 후속, 2026-10-29):
 *   - 같은 출처 fetch·XHR 에 X-WS-Ajax 헤더 부착 → 세션이 끊겼을 때 서버가 로그인 화면 HTML 대신 401 JSON 응답
 *   - 그 401(X-Login-Required: Y)을 받은 저장·삭제 요청에 "다시 로그인" 확인창 (아래 '로그인 만료 처리')
 *
 * 모든 페이지의 <head>에서 가장 먼저 로드되어야 한다.
 */
(function() {
    'use strict';

    var COOKIE_NAME    = 'XSRF-TOKEN';
    var HEADER_NAME    = 'X-XSRF-TOKEN';
    var PARAM_NAME     = '_csrf';
    var UNSAFE_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

    /* ── 쿠키에서 CSRF 토큰 읽기 ── */
    function getToken() {
        var match = document.cookie.match(
            new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)')
        );
        return match ? decodeURIComponent(match[1]) : null;
    }

    function isUnsafe(method) {
        return UNSAFE_METHODS.indexOf((method || 'GET').toUpperCase()) !== -1;
    }

    /* ── 로그인 만료 처리 ──
     * 세션이 끊긴 AJAX 요청에 서버는 로그인 화면 HTML 대신 401 + X-Login-Required 헤더를 돌려준다
     * (LoginRequiredResponder). 예전엔 화면이 HTML 을 JSON 으로 읽다 실패해 "서버 응답이 올바르지 않습니다"
     * 같은 엉뚱한 오류를 띄웠다.
     *  - 저장·삭제(POST 등): 확인창 → [다시 로그인] 이면 로그인 후 지금 화면으로 돌아온다. 바로 이동하지 않는 이유는
     *    길게 쓰던 일지·게시글을 잃지 않도록 [취소] 로 남아 복사할 기회를 주기 위해서다. 확인창이 떠 있는 동안은
     *    화면에 응답을 넘기지 않고(확인창과 오류창이 겹치지 않게), 닫힌 뒤에 넘긴다 — [취소] 뒤 화면의 원래 오류
     *    처리가 돌아야 잠근 버튼이 풀리고 자동 저장 재시도가 이어진다. [다시 로그인] 을 눌렀는데 이동하지 않은
     *    경우(나가기 경고에서 "머무르기")도 STAY_RELEASE_MS 뒤 같은 처리로 풀어 준다. 확인창을 오른쪽 위 X 로 닫으면
     *    WonModal 이 onCancel 을 부르지 않으므로 hidden.bs.modal 로 닫힘 자체를 감지한다.
     *  - 조회(GET): 응답을 그대로 넘긴다. 백그라운드 알림 조회 때문에 읽던 화면이 갑자기 넘어가지 않게. */
    var AJAX_HEADER = 'X-WS-Ajax';
    var LOGIN_REQUIRED_HEADER = 'X-Login-Required';
    var STAY_RELEASE_MS = 10000; // 느린 회선에서 로그인 화면 응답이 늦어도 이동 전에 풀리지 않을 만큼
    var loginPromptShown = false;
    var loginPromptSeq = 0; // 확인창 번호 — 늦게 도착한 옛 확인창의 닫힘·타이머가 새 확인창 대기자를 풀지 않게
    var loginPromptWaiters = []; // 확인창이 닫히면 응답을 넘겨받을 요청들 (동시에 여러 요청이 401 을 받아도 확인창은 하나)

    function isSameOrigin(url) {
        try {
            return new URL(url, window.location.href).origin === window.location.origin;
        } catch (e) {
            return false;
        }
    }

    function isLoginRequired(status, headerValue) {
        return status === 401 && headerValue === 'Y';
    }

    function loginUrl() {
        return '/login.won?expired=Y&returnUrl='
            + encodeURIComponent(window.location.pathname + window.location.search);
    }

    function releaseLoginPrompt(seq) {
        if (seq !== loginPromptSeq || !loginPromptShown) return; // 이미 풀렸거나 다른 확인창 차례
        loginPromptShown = false;
        var waiters = loginPromptWaiters;
        loginPromptWaiters = [];
        waiters.forEach(function(done) { done(); });
    }

    function goLoginOrRelease(seq) {
        window.location.href = loginUrl();
        // 이동이 끝나면 페이지와 함께 사라진다. 이동이 취소됐을 때만(또는 응답이 이보다 늦을 때) 실행된다
        setTimeout(function() { releaseLoginPrompt(seq); }, STAY_RELEASE_MS);
    }

    /** @param onClosed 확인창이 닫힌 뒤(또는 이동이 취소된 뒤) 부를 함수. 생략 가능 */
    function promptRelogin(onClosed) {
        if (onClosed) loginPromptWaiters.push(onClosed);
        if (loginPromptShown) return;
        loginPromptShown = true;
        var seq = ++loginPromptSeq;
        if (!window.WonModal) {
            goLoginOrRelease(seq);
            return;
        }
        if (typeof WonModal.closeLoading === 'function') WonModal.closeLoading();
        var confirmed = false;
        var modal = WonModal.confirm({
            title: '로그인 시간이 지났습니다',
            message: '다시 로그인한 뒤 이 화면으로 돌아옵니다. 작성 중인 내용이 있다면 먼저 복사해 두세요.',
            confirmText: '다시 로그인',
            cancelText: '취소',
            onConfirm: function() { confirmed = true; goLoginOrRelease(seq); },
            onCancel: function() { releaseLoginPrompt(seq); }
        });
        // X 버튼 닫기 대비 — 같은 번호로 한 번만 풀리므로 onCancel 과 겹쳐 불려도 안전하다
        if (modal && modal._element) {
            modal._element.addEventListener('hidden.bs.modal', function() {
                if (!confirmed) releaseLoginPrompt(seq);
            }, { once: true });
        }
    }

    /** 같은 출처 요청에 AJAX 표식을 붙인다. Request 객체 입력은 init.headers 가 원래 헤더를 덮어쓰므로 건드리지 않는다. */
    function markAjax(url, options) {
        if (typeof url !== 'string' && !(url instanceof URL)) return;
        if (!isSameOrigin(url)) return;
        var headers = options.headers;
        if (headers instanceof Headers) {
            if (!headers.has(AJAX_HEADER)) headers.set(AJAX_HEADER, '1');
        } else if (Array.isArray(headers)) {
            headers.push([AJAX_HEADER, '1']);
        } else {
            options.headers = headers || {};
            options.headers[AJAX_HEADER] = '1';
        }
    }

    /* ── 1) fetch 래핑 ── */
    var originalFetch = window.fetch;
    window.fetch = function(url, options) {
        options = options || {};
        var method = options.method || (url instanceof Request ? url.method : 'GET');
        markAjax(url, options);
        if (isUnsafe(options.method)) {
            var token = getToken();
            if (token) {
                if (options.headers instanceof Headers) {
                    if (!options.headers.has(HEADER_NAME)) {
                        options.headers.set(HEADER_NAME, token);
                    }
                } else {
                    options.headers = options.headers || {};
                    if (!options.headers[HEADER_NAME]) {
                        options.headers[HEADER_NAME] = token;
                    }
                }
            }
        }
        return originalFetch.call(this, url, options).then(function(response) {
            if (isLoginRequired(response.status, response.headers.get(LOGIN_REQUIRED_HEADER)) && isUnsafe(method)) {
                // 확인창이 닫힌 뒤에 응답을 넘긴다(위 설명)
                return new Promise(function(resolve) {
                    promptRelogin(function() { resolve(response); });
                });
            }
            return response;
        });
    };

    /* ── 2) XMLHttpRequest 래핑 ── */
    var originalXhrOpen = XMLHttpRequest.prototype.open;
    var originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
        this._csrfMethod = method;
        this._wsSameOrigin = isSameOrigin(url);
        return originalXhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
        if (isUnsafe(this._csrfMethod)) {
            var token = getToken();
            if (token) {
                this.setRequestHeader(HEADER_NAME, token);
            }
        }
        if (this._wsSameOrigin) {
            this.setRequestHeader(AJAX_HEADER, '1');
            // XHR 은 호출부 콜백을 막을 수 없어 확인창만 띄운다(저장·삭제 요청일 때만)
            if (isUnsafe(this._csrfMethod)) {
                this.addEventListener('load', function() {
                    if (isLoginRequired(this.status, this.getResponseHeader(LOGIN_REQUIRED_HEADER))) {
                        promptRelogin();
                    }
                });
            }
        }
        return originalXhrSend.apply(this, arguments);
    };

    /* ── 3) 동적 폼 유틸 ── */
    window.WsCsrf = {
        /** 동적 생성 form에 CSRF hidden 필드를 추가한다. form.submit() 전에 호출. */
        appendToForm: function(form) {
            var token = getToken();
            if (token && form) {
                var input = document.createElement('input');
                input.type = 'hidden';
                input.name = PARAM_NAME;
                input.value = token;
                form.appendChild(input);
            }
        }
    };
})();
